import { type ComponentType, createElement, type ReactNode } from "react";
import type { PickerStatus } from "@design-system/Picker/Picker.lib";
import {
    CustomListEditor,
    type DataGridListCustomEdit,
} from "../editing/list/editors/CustomListEditor";
import { DateListEditor } from "../editing/list/editors/DateListEditor";
import { DurationListEditor } from "../editing/list/editors/DurationListEditor";
import { PickerListEditor } from "../editing/list/editors/PickerListEditor";
import { PopoverListEditor } from "../editing/list/editors/PopoverListEditor";
import { TextListEditor } from "../editing/list/editors/TextListEditor";
import type { DataGridCellLayout } from "./types";

/** Saves a list edit; may confirm first, update more fields, or wait. The grid never awaits it. */
export type DataGridListCommit<TData, TValue> = (change: {
    row: TData;
    value: TValue;
    previousValue: TValue;
}) => void | Promise<void>;

/** What every `col.edit.*` factory takes. */
export interface DataGridListEditBase<TData, TValue> {
    /** What the editor edits; may differ from the column's display value (an id, not the person). */
    value: (row: TData) => TValue;
    commit: DataGridListCommit<TData, TValue>;
    /** Revealed on hover when the value is empty: "Add due date". */
    placeholder: string;
}

/** A popup editor's cell layout, `fill` unless it opts out. */
export interface DataGridListEditLayout {
    /** `fit` hugs a textless display (an icon, avatars) instead of filling the cell. */
    layout?: DataGridCellLayout;
}

/** The popup an editor opens, for the cell's `aria-haspopup`; none for inline text. */
export type DataGridListEditorPopup = "listbox" | "dialog";

/** What an editor (or a custom widget) spreads onto `ListEditTrigger`, with its own trigger props. */
export interface DataGridListTriggerContent {
    /** The column's normal display for the row. */
    display: ReactNode;
    isEmpty: boolean;
    placeholder: string;
    /** The column's name when the display has none of its own (avatars). */
    "aria-label"?: string;
}

export interface DataGridListEditorProps<TData> {
    row: TData;
    /** The column's name; an inline editor's accessible name. */
    columnLabel: string;
    trigger: DataGridListTriggerContent;
    isOpen: boolean;
    onOpenChange: (isOpen: boolean) => void;
    /**
     * Closes the editor, focuses the cell, then runs `commit` (a
     * `runListCommit`), so a confirmation it opens returns focus to the cell.
     */
    onCloseAndCommit: (commit: () => void) => void;
}

/** What `col.edit.*` returns; closes over the value type. */
export interface DataGridListEditor<TData> {
    popup: DataGridListEditorPopup | undefined;
    layout: DataGridCellLayout;
    placeholder: string;
    isEmpty: (row: TData) => boolean;
    Editor: ComponentType<DataGridListEditorProps<TData>>;
}

export interface DataGridListPopoverEdit<TData, TValue>
    extends DataGridListEditBase<TData, TValue>, DataGridListEditLayout {
    /** The popover body; `commit(value)` closes it and saves, `close()` closes it unchanged. */
    content: (props: {
        row: TData;
        value: TValue;
        commit: (value: TValue) => void;
        close: () => void;
    }) => ReactNode;
}

/** One row of a picker editor's list. */
export interface DataGridListOption<TOption extends string | number> {
    value: TOption;
    label: string;
    /** An icon or avatar before the label. */
    leading?: ReactNode;
    description?: string;
    disabled?: boolean;
    /** Shown on hover over a `disabled` option. */
    disabledReason?: string;
}

/**
 * Where a picker's options come from: the row, or a hook the open editor
 * runs with its search text (`status` swaps in the loading or error row).
 */
type DataGridListOptionsSource<TData, TOption extends string | number> =
    | {
          options: (row: TData) => ReadonlyArray<DataGridListOption<TOption>>;
          useOptions?: undefined;
      }
    | {
          useOptions: (
              row: TData,
              search: string,
          ) => {
              options: ReadonlyArray<DataGridListOption<TOption>>;
              status: PickerStatus;
          };
          options?: undefined;
      };

/**
 * `col.edit.picker`: one option or `null` (a pick closes, then commits), or
 * with `multiple` the whole array on every check and uncheck (stays open).
 */
export type DataGridListPickerEdit<
    TData,
    TOption extends string | number,
> = DataGridListOptionsSource<TData, TOption> &
    DataGridListEditLayout &
    (
        | (DataGridListEditBase<TData, TOption | null> & {
              multiple?: false;
              /** A first row that commits `null`: "No stage", with an optional icon before it. */
              emptyOption?: { label: string; leading?: ReactNode };
          })
        | (DataGridListEditBase<TData, readonly TOption[]> & {
              /**
               * Each toggle starts from `value(row)`, so `commit` must update
               * the row synchronously (optimistically); a row that waits for
               * the server drops the earlier of two quick toggles.
               */
              multiple: true;
              emptyOption?: undefined;
          })
    );

/** A date editor's bounds from the row, as `dateOnly` strings. */
interface DataGridListDateBounds<TData> {
    minDate?: (row: TData) => string | undefined;
    maxDate?: (row: TData) => string | undefined;
}

/**
 * `col.edit.date`: a `dateOnly` (`YYYY-MM-DD`) day, never a timestamp; `null`
 * once cleared, unless `isClearable: false`.
 */
export type DataGridListDateEdit<TData> = DataGridListDateBounds<TData> &
    (
        | (DataGridListEditBase<TData, string | null> & {
              isClearable?: true;
          })
        | (DataGridListEditBase<TData, string> & {
              isClearable: false;
          })
    );

/** `col.edit.duration`: minutes, `null` once removed, unless `isClearable: false`. */
export type DataGridListDurationEdit<TData> = DataGridListEditBase<
    TData,
    number | null
> & {
    /** Names the popup and its input: "Estimate". */
    label: string;
} & (
        | {
              isClearable?: true;
              /** The clearable footer button: "Remove estimate". */
              removeLabel: string;
          }
        | { isClearable: false; removeLabel?: undefined }
    );

/** Empty values show the placeholder: `null`, `""`, and empty arrays. */
const isEmptyListValue = <TValue>(value: TValue) =>
    value === null ||
    value === undefined ||
    value === "" ||
    (Array.isArray(value) && value.length === 0);

/**
 * The editor every factory returns: its popup, its layout (a popup's trigger
 * fills the cell unless the column passes `layout: "fit"`), and its empty check.
 */
const toListEditor = <TData, TValue>({
    edit,
    popup,
    layout = "fill",
    Editor,
}: {
    edit: Pick<DataGridListEditBase<TData, TValue>, "value" | "placeholder">;
    popup: DataGridListEditorPopup | undefined;
    layout?: DataGridCellLayout;
    Editor: ComponentType<DataGridListEditorProps<TData>>;
}): DataGridListEditor<TData> => ({
    popup,
    layout,
    placeholder: edit.placeholder,
    isEmpty: (row) => isEmptyListValue(edit.value(row)),
    Editor,
});

/**
 * Editor factories for a column's `edit`, bound to `TData`. Each is generic
 * over the value it edits, so `commit` is typed without the column knowing.
 */
export const createListEditors = <TData>() => ({
    popover: <TValue>(
        options: DataGridListPopoverEdit<TData, TValue>,
    ): DataGridListEditor<TData> => {
        const PopoverEditor = (props: DataGridListEditorProps<TData>) =>
            createElement(PopoverListEditor<TData, TValue>, {
                ...props,
                edit: options,
            });
        return toListEditor({
            edit: options,
            popup: "dialog",
            layout: options.layout,
            Editor: PopoverEditor,
        });
    },
    picker: <TOption extends string | number>(
        options: DataGridListPickerEdit<TData, TOption>,
    ): DataGridListEditor<TData> => {
        const PickerEditor = (props: DataGridListEditorProps<TData>) =>
            createElement(PickerListEditor<TData, TOption>, {
                ...props,
                edit: options,
            });
        return toListEditor<TData, TOption | null | readonly TOption[]>({
            edit: options,
            popup: options.useOptions === undefined ? "listbox" : "dialog",
            layout: options.layout,
            Editor: PickerEditor,
        });
    },
    date: (options: DataGridListDateEdit<TData>): DataGridListEditor<TData> => {
        const DateEditor = (props: DataGridListEditorProps<TData>) =>
            createElement(DateListEditor<TData>, { ...props, edit: options });
        return toListEditor<TData, string | null>({
            edit: options,
            popup: "dialog",
            Editor: DateEditor,
        });
    },
    duration: (
        options: DataGridListDurationEdit<TData>,
    ): DataGridListEditor<TData> => {
        const DurationEditor = (props: DataGridListEditorProps<TData>) =>
            createElement(DurationListEditor<TData>, {
                ...props,
                edit: options,
            });
        return toListEditor({
            edit: options,
            popup: "dialog",
            Editor: DurationEditor,
        });
    },
    /**
     * Text edited in place, no popup. Its trigger fits the text, so the rest
     * of the cell opens the row.
     */
    text: (
        options: DataGridListEditBase<TData, string>,
    ): DataGridListEditor<TData> => {
        const TextEditor = (props: DataGridListEditorProps<TData>) =>
            createElement(TextListEditor<TData>, { ...props, edit: options });
        return toListEditor({
            edit: options,
            popup: undefined,
            layout: "fit",
            Editor: TextEditor,
        });
    },
    /** A widget that owns its popup; `render` mounts once the cell first opens. */
    custom: <TValue>(
        options: DataGridListCustomEdit<TData, TValue>,
    ): DataGridListEditor<TData> => {
        const CustomEditor = (props: DataGridListEditorProps<TData>) =>
            createElement(CustomListEditor<TData, TValue>, {
                ...props,
                edit: options,
            });
        return toListEditor({
            edit: options,
            popup: options.popup,
            layout: options.layout,
            Editor: CustomEditor,
        });
    },
});
